import { initAuth, isSignedIn, signIn } from "./auth";
import { api, setApiUrl } from "./api";
import { renderApp, renderWho } from "./ui";
import { openHelpModal, closeHelpModal, type PortalConfig } from "./help";

interface AppConfig {
  googleClientId: string;
  apiUrl: string;
  maxRetainedVersions?: number;
  skillVersion?: string;
  deployedAt?: string;
}

// Config is baked into index.html as <html data-*> attributes at deploy time
// (see lib/portal-stack.ts). Reading synchronously avoids a /config.json
// round-trip on every page load.
function readConfig(): AppConfig {
  const d = document.documentElement.dataset;
  const googleClientId = d.googleClientId ?? "";
  const apiUrl = d.apiUrl ?? "";
  if (!googleClientId || !apiUrl) {
    throw new Error("App config missing — index.html was not configured at deploy time");
  }
  const max = d.maxRetainedVersions ? parseInt(d.maxRetainedVersions, 10) : undefined;
  return {
    googleClientId,
    apiUrl,
    maxRetainedVersions: Number.isFinite(max as number) ? max : undefined,
    skillVersion: d.skillVersion || undefined,
    deployedAt: d.deployedAt || undefined,
  };
}

async function main() {
  const root = document.getElementById("app")!;
  try {
    const cfg = readConfig();

    setApiUrl(cfg.apiUrl);
    await initAuth({ googleClientId: cfg.googleClientId, apiUrl: cfg.apiUrl });

    if (!isSignedIn()) {
      document.querySelector("header")!.style.display = "none";
      root.innerHTML = `
        <div class="signin-card">
          <h1>AI Prototypes</h1>
          <p class="signin-subtitle">Upload and share Claude-generated HTML prototypes</p>
          <button id="signin" class="google-btn">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#34A853" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#FBBC05" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            Sign in with Google
          </button>
        </div>
      `;
      document.getElementById("signin")!.addEventListener("click", () => {
        signIn().catch((err) => {
          const errDiv = document.createElement("div");
          errDiv.className = "error";
          errDiv.textContent = (err as Error).message;
          root.replaceChildren(errDiv);
        });
      });
      return;
    }

    document.querySelector("header")!.style.display = "";
    renderWho();
    renderFooter(cfg);

    // Kick off the prototypes fetch before DOM setup — it races the
    // synchronous renderApp work, and more importantly starts the API
    // round-trip as early as possible. Swallow rejections here so an
    // unhandled-rejection warning doesn't fire while renderApp wires up;
    // the same promise is awaited inside renderApp which surfaces the error.
    const initialListPromise = api.list();
    initialListPromise.catch(() => { /* handled in renderApp */ });

    // App view always renders. Help is a modal overlay toggled by #help hash —
    // not a separate route — so the prototype list stays visible behind it and
    // back-button / esc / overlay-click all close cleanly without re-rendering.
    await renderApp(root, {
      maxRetainedVersions: cfg.maxRetainedVersions,
      initialListPromise,
    });

    async function syncHelpModal() {
      if (location.hash === "#help") {
        // Refetch /config.json on every open so the modal reflects the
        // currently deployed config (skillVersion, uploadLimits, etc.) — the
        // file is served with CACHING_DISABLED so this is cheap and the
        // user expects post-deploy values to show up immediately.
        const resp = await fetch("/config.json", { cache: "no-store" });
        if (!resp.ok) throw new Error("failed to load /config.json for help");
        const helpCfg = (await resp.json()) as PortalConfig;
        openHelpModal(helpCfg);
      } else {
        closeHelpModal();
      }
    }
    // Wrap in a non-async listener so a fetch rejection doesn't escape as an
    // unhandled rejection. Errors render into #app as the user-visible error
    // div (same handler the outer try/catch uses).
    function onHashChange() {
      void syncHelpModal().catch((e) => {
        const errDiv = document.createElement("div");
        errDiv.className = "error";
        errDiv.textContent = `Help: ${(e as Error).message}`;
        root.replaceChildren(errDiv);
      });
    }
    window.addEventListener("hashchange", onHashChange);
    onHashChange();
  } catch (e) {
    const errDiv = document.createElement("div");
    errDiv.className = "error";
    errDiv.textContent = (e as Error).message;
    root.replaceChildren(errDiv);
  }
}

function renderFooter(cfg: AppConfig): void {
  const el = document.getElementById("site-footer");
  if (!el) return;
  const parts: string[] = [];
  if (cfg.deployedAt) {
    const d = new Date(cfg.deployedAt);
    if (!isNaN(d.getTime())) {
      const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
      parts.push(`Last updated ${date} ${time}`);
    }
  }
  if (cfg.skillVersion) parts.push(`v${cfg.skillVersion}`);
  if (parts.length) el.textContent = parts.join(" · ");
}

main();
